import { FC, useEffect } from 'react';

import { formatCostInCents, getFormattedIntNumber } from 'utils/clickme';

import { Card, GridRow, Skeleton, Text, VSpacing } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { fetchStatistic } from 'components/common/CampaignStatisticCard/fetchStatistic';
import withErrorBoundary from 'components/common/ErrorBoundary/withErrorBoundary';

import { resetCampaignStatistic } from 'store/models/campaignStatistic';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/CampaignStatisticCard/StatisticCard.module.less';

const TrlKeys = {
    title: 'CampaignStatisticCard.title',
    impressions: 'CampaignStatisticCard.impressions',
    responses: 'CampaignStatisticCard.responses',
    spent: 'CampaignStatisticCard.spent',
    clicks: 'CampaignStatisticCard.clicks',
    currency: 'currency.short',
};

interface StatisticCardItemProps {
    title: string;
    value: string;
    loading: boolean;
}

const StatisticCardItem: FC<StatisticCardItemProps> = ({ title, value, loading }) => {
    return (
        <div className={styles.column}>
            <Text style="secondary" typography="label-2-regular">
                {title}
            </Text>
            <Skeleton width={78} height={26} borderRadius={8} loading={loading}>
                <Text style="primary" typography="title-5-semibold">
                    {value}
                </Text>
            </Skeleton>
        </div>
    );
};

interface StatisticCardProps {
    campaignId: string;
}

const StatisticCardComponent: FC<StatisticCardProps> = ({ campaignId }) => {
    const { impressions, clicks, spent, responses } = useSelector((state) => state.campaignStatistic.statistic);
    const loading = useSelector((state) => state.campaignStatistic.loading);
    const isError = useSelector((state) => state.campaignStatistic.isError);

    const trl = useTrls();
    const dispatch = useDispatch();

    useEffect(() => {
        if (campaignId) {
            void dispatch(fetchStatistic(campaignId));
        }
        return () => {
            dispatch(resetCampaignStatistic(null));
        };
    }, [campaignId, dispatch]);

    if (isError || !campaignId) {
        return null;
    }

    return (
        <Card padding={24} borderRadius={16} stretched borderStyle="phantom" borderWidth="default">
            <Text style="primary" typography="title-5-semibold">
                {trl(TrlKeys.title)}
            </Text>

            <VSpacing default={24} />

            <GridRow rowGap={16}>
                <StatisticCardItem
                    title={trl(TrlKeys.impressions)}
                    value={getFormattedIntNumber(impressions)}
                    loading={loading}
                />
                <StatisticCardItem
                    title={trl(TrlKeys.clicks)}
                    value={getFormattedIntNumber(clicks)}
                    loading={loading}
                />
            </GridRow>

            <VSpacing default={24} />

            <GridRow rowGap={16}>
                <StatisticCardItem
                    title={trl(TrlKeys.responses)}
                    value={getFormattedIntNumber(responses)}
                    loading={loading}
                />
                <StatisticCardItem
                    title={trl(TrlKeys.spent)}
                    value={`${formatCostInCents(spent, ' ')} ${trl(TrlKeys.currency)}`}
                    loading={loading}
                />
            </GridRow>
        </Card>
    );
};

export const StatisticCard = withErrorBoundary(StatisticCardComponent as unknown as FC<Record<string, unknown>>);
